import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  Badge,
  People,
  LocalCarWash,
  Pending,
  PersonOff,
  Wifi,
  Visibility,
  Lock,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import adminService from "../../../services/admin.service";
import ownerService from "../../../services/owner.service";
import { IAdmin } from "../../../types/admin.types";
import { IOwnerObject } from "../../../services/api.types";
import { STATUS_SUCCESS } from "../../../constants/responseTypes";

interface IRMStats {
  total: number;
  online: number;
  pending: number;
  withoutEmployee: number;
  totalCarWashes: number;
}

const RMDetail = () => {
  const { t } = useTranslation();
  const { adminId } = useParams<{ adminId: string }>();
  const navigate = useNavigate();

  const [rm, setRm] = useState<IAdmin | null>(null);
  const [owners, setOwners] = useState<IOwnerObject[]>([]);
  const [stats, setStats] = useState<IRMStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Change-password dialog (SuperAdmin-only entry point — this page's route is
  // already wrapped in SuperAdminRoute in index.tsx). Backend's changeAdminPassword
  // only checks currentPassword when an admin changes their OWN password
  // (req.params.id === req.admin._id); for a super_admin changing someone else's,
  // it's required to be present (non-empty) but is never verified, so newPassword
  // is sent for both fields rather than inventing a meaningless placeholder value.
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  const closePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError(null);
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    if (newPassword.length < 8) {
      setPasswordError(t("rm.passwordTooShort"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t("rm.passwordMismatch"));
      return;
    }
    try {
      setPasswordSubmitting(true);
      await adminService.changePassword(adminId!, {
        currentPassword: newPassword,
        newPassword,
        confirmPassword,
      });
      closePasswordDialog();
      setPasswordSuccess(t("rm.passwordChangedSuccess"));
    } catch (err: any) {
      setPasswordError(err.message || t("serviceErrors.changePasswordFailed"));
    } finally {
      setPasswordSubmitting(false);
    }
  };

  useEffect(() => {
    if (!adminId) return;
    fetchData();
    // Re-runs only when adminId changes; fetchData is a plain
    // (non-memoized) function recreated every render, so adding it here
    // would refetch on every render instead of only when the route param
    // changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [rmData, ownersRes] = await Promise.all([
        adminService.getAdminById(adminId!),
        ownerService.getOwners(),
      ]);

      setRm(rmData);

      const allOwners: IOwnerObject[] =
        ownersRes.status === STATUS_SUCCESS ? ownersRes.data : [];
      const rmOwners = allOwners.filter(
        (o) => o.relationshipManager?._id === adminId
      );
      setOwners(rmOwners);

      const since = new Date(Date.now() - 2 * 60 * 1000);
      // Pending owners are not on the roster yet: leave them out of Total and
      // Without-employees (they keep their own Pending number).
      const approvedOwners = rmOwners.filter(
        (o) => o.registrationStatus !== "pending_approval"
      );
      setStats({
        total: approvedOwners.length,
        online: rmOwners.filter(
          (o) => o.lastSeenAt && new Date(o.lastSeenAt) >= since
        ).length,
        pending: rmOwners.filter(
          (o) => o.registrationStatus === "pending_approval"
        ).length,
        withoutEmployee: approvedOwners.filter(
          (o) => (o.employees?.length ?? 0) === 0
        ).length,
        totalCarWashes: rmOwners.reduce(
          (sum, o) => sum + (o.carWashCount ?? o.carWashes?.length ?? 0),
          0
        ),
      });
    } catch (err: any) {
      setError(err.message || t("rm.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !rm) {
    return (
      <Box className="p-4">
        <Alert severity="error">{error || t("rm.notFound")}</Alert>
      </Box>
    );
  }

  const statCards = [
    {
      label: t("rm.statTotalOwners"),
      value: stats?.total ?? 0,
      icon: <People />,
      color: "primary.main",
    },
    {
      label: t("rm.statOnlineNow"),
      value: stats?.online ?? 0,
      icon: <Wifi />,
      color: "success.main",
    },
    {
      label: t("rm.statPendingApproval"),
      value: stats?.pending ?? 0,
      icon: <Pending />,
      color: "warning.main",
    },
    {
      label: t("rm.statNoEmployees"),
      value: stats?.withoutEmployee ?? 0,
      icon: <PersonOff />,
      color: "error.main",
    },
    {
      label: t("rm.statTotalCarWashes"),
      value: stats?.totalCarWashes ?? 0,
      icon: <LocalCarWash />,
      color: "info.main",
    },
  ];

  return (
    <Box className="p-4">
      {/* Header */}
      <Box className="flex items-center justify-between gap-3 mb-6">
        <Box className="flex items-center gap-3">
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            {t("rm.dashboardTitle")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Lock />}
          onClick={() => {
            setPasswordSuccess(null);
            setPasswordDialogOpen(true);
          }}
        >
          {t("rm.changePassword")}
        </Button>
      </Box>

      {passwordSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setPasswordSuccess(null)}>
          {passwordSuccess}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Box className="flex items-center gap-3 mb-4">
                <Avatar
                  sx={{ bgcolor: "primary.main", width: 56, height: 56, fontSize: "1.4rem" }}
                >
                  {rm.firstName?.[0]?.toUpperCase() ?? rm.username[0].toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {rm.firstName && rm.lastName
                      ? `${rm.firstName} ${rm.lastName}`
                      : rm.username}
                  </Typography>
                  <Chip
                    label={t("admins.roleRelationshipManager")}
                    color="primary"
                    size="small"
                  />
                </Box>
              </Box>

              <List dense>
                <ListItem>
                  <ListItemIcon><Badge fontSize="small" /></ListItemIcon>
                  <ListItemText primary={t("rm.username")} secondary={rm.username} />
                </ListItem>
                {rm.email && (
                  <ListItem>
                    <ListItemIcon><Email fontSize="small" /></ListItemIcon>
                    <ListItemText primary={t("rm.email")} secondary={rm.email} />
                  </ListItem>
                )}
                {rm.phone && (
                  <ListItem>
                    <ListItemIcon><Phone fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary={t("rm.phone")}
                      secondary={`${rm.country_code ?? ""} ${rm.phone}`.trim()}
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={t("common.status")}
                    secondary={
                      <Chip
                        label={
                          rm.active ? t("status.active") : t("status.inactive")
                        }
                        color={rm.active ? "success" : "default"}
                        size="small"
                      />
                    }
                  />
                </ListItem>
                {rm.lastLogin && (
                  <ListItem>
                    <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary={t("rm.lastLogin")}
                      secondary={new Date(rm.lastLogin).toLocaleString()}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Stats Cards */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Grid container spacing={2}>
            {statCards.map((s) => (
              <Grid key={s.label} size={{ xs: 6, sm: 4 }}>
                <Card>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Avatar
                      sx={{ bgcolor: s.color, mx: "auto", mb: 1, width: 40, height: 40 }}
                    >
                      {s.icon}
                    </Avatar>
                    <Typography variant="h4" fontWeight="bold">
                      {s.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {s.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Assigned Owners Table */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {t("rm.assignedOwners", { count: owners.length })}
          </Typography>
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("common.name")}</TableCell>
                    <TableCell>{t("common.phone")}</TableCell>
                    <TableCell align="center">{t("rm.colCarWashes")}</TableCell>
                    <TableCell align="center">{t("rm.colEmployees")}</TableCell>
                    <TableCell>{t("rm.colRegistration")}</TableCell>
                    <TableCell align="right">{t("common.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {owners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {t("rm.noOwnersAssigned")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    owners.map((owner) => (
                      <TableRow
                        key={owner._id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(`/dashboard/manage-owners/${owner._id}/info`)
                        }
                      >
                        <TableCell>
                          {owner.firstName} {owner.lastName}
                        </TableCell>
                        <TableCell>
                          {owner.country_code} {owner.phone}
                        </TableCell>
                        <TableCell align="center">
                          {owner.carWashCount ?? owner.carWashes?.length ?? 0}
                        </TableCell>
                        <TableCell align="center">
                          {owner.employees?.length ?? 0}
                        </TableCell>
                        <TableCell>
                          <RegistrationChip status={owner.registrationStatus} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t("admins.viewOwner")}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `/dashboard/manage-owners/${owner._id}/info`
                                );
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={passwordDialogOpen} onClose={closePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t("rm.changePassword")}</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError(null)}>
              {passwordError}
            </Alert>
          )}
          <TextField
            fullWidth
            type="password"
            label={t("rm.newPassword")}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="dense"
            required
            autoFocus
          />
          <TextField
            fullWidth
            type="password"
            label={t("rm.confirmNewPassword")}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="dense"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePasswordDialog} disabled={passwordSubmitting}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={passwordSubmitting || !newPassword || !confirmPassword}
          >
            {t("rm.changePassword")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  admin_created: "rm.regAdminCreated",
  pending_approval: "rm.regPendingApproval",
  approved: "rm.regApproved",
  rejected: "rm.regRejected",
};

const STATUS_COLORS: Record<
  string,
  "success" | "warning" | "error" | "info" | "default"
> = {
  admin_created: "info",
  pending_approval: "warning",
  approved: "success",
  rejected: "error",
};

const RegistrationChip = ({ status }: { status?: string }) => {
  const { t } = useTranslation();
  return (
    <Chip
      label={
        status
          ? STATUS_LABEL_KEYS[status]
            ? t(STATUS_LABEL_KEYS[status])
            : status
          : "—"
      }
      color={status ? (STATUS_COLORS[status] ?? "default") : "default"}
      size="small"
    />
  );
};

export default RMDetail;
